<template>
  <SettingsPageLayout>
    <DetailForm
      v-if="loaded"
      title="Event Scheduling"
      description="Manage how Candidates and Teammates schedule various events related to the recruiting process with your team/brokerage."
      action="Save"
      hidecancel="true"
      @handle-action="updateTeam"
    >
      <ToggleInput
        label="Allow Qualified Candidates to Schedule Immediately"
        description="If a qualified candidate applies, redirect them to your calendar so they can schedule their phone screen immediately."
        :model-value="team.allowQualifiedCandidatesToScheduleImmediately"
        @update:model-value="team.allowQualifiedCandidatesToScheduleImmediately = $event"
      />
      <RadioSelect
        v-model="team.calendarOption"
        label="Scheduling Platform"
        :options="[
          {
            label: 'Humaniz',
            description:
              'When Humaniz is selected as the primary form of scheduling all scheduling happens through our platform. Users must be assigned to the respective events for scheduling to work for that event type.',
          },
          {
            label: 'Calendly',
            description:
              'When Calendly is selected as the primary form of scheduling all scheduling happens through their platform. Calendly api token and link will be required to go live with syndication.',
          },
        ]"
      />
      <template v-if="team.calendarOption == 'Humaniz'">
        <Accordion type="multiple" collapsible>
          <EventOptionsItem
            v-model:duration="team.phoneScreenEventDuration"
            v-model:name="team.phoneScreenEventName"
            v-model:description="team.phoneScreenEventDescription"
            v-model:location="team.phoneScreenEventLocation"
            v-model:address="team.phoneScreenEventAddress"
            value="phone-screen"
            label="Phone Screen"
            :slug="team.slug"
            event-type="phone_screen"
          />
          <EventOptionsItem
            v-model:duration="team.interviewEventDuration"
            v-model:name="team.interviewEventName"
            v-model:description="team.interviewEventDescription"
            v-model:location="team.interviewEventLocation"
            v-model:address="team.interviewEventAddress"
            value="interview"
            label="Interview"
            :slug="team.slug"
            event-type="interview"
          />
          <EventOptionsItem
            v-model:duration="team.pipelineReviewEventDuration"
            v-model:name="team.pipelineReviewEventName"
            v-model:description="team.pipelineReviewEventDescription"
            v-model:location="team.pipelineReviewEventLocation"
            v-model:address="team.pipelineReviewEventAddress"
            value="pipeline-review"
            label="Pipeline Review"
            :slug="team.slug"
            event-type="pipeline_review"
          />
        </Accordion>
      </template>
      <template v-else>
        <div class="space-y-4">
          <div class="space-y-1">
            <Label>Calendly Link</Label>
            <Input v-model="team.calendlyLink" placeholder="https://calendly.com/ehomesg/15-min-phone-interview" />
          </div>
          <div class="space-y-1">
            <Label>Calendly Api Token</Label>
            <Textarea v-model="team.calendlyApiToken" :rows="3" placeholder="eHomes" />
          </div>
          <div class="space-y-1">
            <Label>Followup Calendly Link</Label>
            <Input
              v-model="team.followupCalendlyLink"
              placeholder="https://calendly.com/ehomesg/15-min-phone-interview"
            />
          </div>
        </div>
      </template>
    </DetailForm>
  </SettingsPageLayout>
</template>

<script setup lang="ts">
import { reactive, ref, onMounted } from 'vue';
import SettingsPageLayout from '../../layouts/SettingsPageLayout/index.vue';
import RailsUtility from '../../../utilities/rails';
import DetailForm from '../../shared/DetailForm.vue';
import RadioSelect from '../../shared/RadioSelect.vue';
import { Accordion } from '@/components/ui/accordion';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import EventOptionsItem from './EventOptionsItem.vue';
import { toast } from 'vue-sonner';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';
import { useTeamStore } from '@/stores/team';
import { useCurrentUserStore } from '@/stores/currentUser';
import { useUserCalendarStore } from '@/stores/userCalendar';

const loadingSpinnerStore = useLoadingSpinnerStore();
const teamStore = useTeamStore();
const currentUserStore = useCurrentUserStore();
const userCalendarStore = useUserCalendarStore();
const loaded = ref(false);

const team = reactive({
  id: '',
  slug: '',
  allowQualifiedCandidatesToScheduleImmediately: false,
  calendarOption: '',
  phoneScreenEventName: '',
  phoneScreenEventDescription: '',
  phoneScreenEventDuration: '',
  phoneScreenEventLocation: '',
  phoneScreenEventAddress: '',
  interviewEventName: '',
  interviewEventDescription: '',
  interviewEventDuration: '',
  interviewEventLocation: '',
  interviewEventAddress: '',
  pipelineReviewEventDuration: '',
  pipelineReviewEventName: '',
  pipelineReviewEventDescription: '',
  pipelineReviewEventLocation: '',
  pipelineReviewEventAddress: '',
  calendlyLink: '',
  calendlyApiToken: '',
  followupCalendlyLink: '',
});

async function getTeam() {
  loadingSpinnerStore.showLoadingSpinner();
  const response = await fetch('/api/team');
  loadingSpinnerStore.hideLoadingSpinner();
  const data = await response.json();

  team.id = data.id;
  team.slug = data.record.slug;
  team.allowQualifiedCandidatesToScheduleImmediately = data.record.allow_qualified_candidates_to_schedule_immediately;
  team.calendarOption = data.record.calendar_option;
  team.phoneScreenEventName = data.record.phone_screen_event_name;
  team.phoneScreenEventDescription = data.record.phone_screen_event_description;
  team.phoneScreenEventDuration = RailsUtility.convertFromEnumFormat(data.record.phone_screen_event_duration);
  team.phoneScreenEventLocation = RailsUtility.convertFromEnumFormat(data.record.phone_screen_event_location);
  team.phoneScreenEventAddress = data.record.phone_screen_event_address;
  team.interviewEventName = data.record.interview_event_name;
  team.interviewEventDescription = data.record.interview_event_description;
  team.interviewEventDuration = RailsUtility.convertFromEnumFormat(data.record.interview_event_duration);
  team.interviewEventLocation = RailsUtility.convertFromEnumFormat(data.record.interview_event_location);
  team.interviewEventAddress = data.record.interview_event_address;
  team.pipelineReviewEventName = data.record.pipeline_review_event_name;
  team.pipelineReviewEventDescription = data.record.pipeline_review_event_description;
  team.pipelineReviewEventDuration = RailsUtility.convertFromEnumFormat(data.record.pipeline_review_event_duration);
  team.pipelineReviewEventLocation = RailsUtility.convertFromEnumFormat(data.record.pipeline_review_event_location);
  team.pipelineReviewEventAddress = data.record.pipeline_review_event_address;
  team.calendlyLink = data.record.calendly_link;
  team.calendlyApiToken = data.record.calendly_api_token;
  team.followupCalendlyLink = data.record.calendly_link_followup;

  loaded.value = true;

  const userId = currentUserStore.currentUser.id;
  if (userId) {
    userCalendarStore.loadCalendarAuthStatus({ id: userId.toString() });
  }
}

async function updateTeam() {
  loadingSpinnerStore.showLocalLoadingSpinner();
  const response = await fetch('/api/team', {
    method: 'PUT',
    body: JSON.stringify({
      allow_qualified_candidates_to_schedule_immediately: team.allowQualifiedCandidatesToScheduleImmediately,
      calendar_option: team.calendarOption,
      phone_screen_event_name: team.phoneScreenEventName,
      phone_screen_event_description: team.phoneScreenEventDescription,
      phone_screen_event_duration: RailsUtility.convertToEnumFormat(team.phoneScreenEventDuration),
      phone_screen_event_location: RailsUtility.convertToEnumFormat(team.phoneScreenEventLocation),
      phone_screen_event_address: team.phoneScreenEventAddress,
      interview_event_name: team.interviewEventName,
      interview_event_description: team.interviewEventDescription,
      interview_event_duration: RailsUtility.convertToEnumFormat(team.interviewEventDuration),
      interview_event_location: RailsUtility.convertToEnumFormat(team.interviewEventLocation),
      interview_event_address: team.interviewEventAddress,
      pipeline_review_event_name: team.pipelineReviewEventName,
      pipeline_review_event_description: team.pipelineReviewEventDescription,
      pipeline_review_event_duration: RailsUtility.convertToEnumFormat(team.pipelineReviewEventDuration),
      pipeline_review_event_location: RailsUtility.convertToEnumFormat(team.pipelineReviewEventLocation),
      pipeline_review_event_address: team.pipelineReviewEventAddress,
      calendly_link: team.calendlyLink,
      calendly_api_token: team.calendlyApiToken,
      calendly_link_followup: team.followupCalendlyLink,
    }),
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': (document.querySelector('[name="csrf-token"]') as HTMLMetaElement)?.content ?? '',
    },
  });
  const data = await response.json();
  loadingSpinnerStore.hideLocalLoadingSpinner();

  if (data.status != 'error') {
    teamStore.reloadTeam();
    toast.success('Scheduling settings updated', {
      action: { label: 'Dismiss' },
      description: 'Your Scheduling settings were successfully updated.  Use the button below to dismiss this alert.',
    });
  } else {
    toast.error('Scheduling settings update error', {
      action: { label: 'Dismiss' },
      description: data.message,
    });
  }
}

onMounted(getTeam);
</script>
